<template>
  <div class="admin-speakers">
    <!-- 搜索和筛选区域 -->
    <div class="search-section">
      <el-card>
        <div class="search-form">
          <el-row :gutter="20">
            <el-col :span="8">
              <el-input v-model="searchForm.keyword" :placeholder="$t('admin.speakers.search.placeholder')" clearable
                @keyup.enter="handleSearch">
                <template #prefix>
                  <el-icon><Search /></el-icon>
                </template>
              </el-input>
            </el-col>
            <el-col :span="6">
              <el-select v-model="searchForm.group_name" placeholder="选择分组" clearable style="width: 100%">
                <el-option
                  v-for="group in groupOptions"
                  :key="group"
                  :label="group"
                  :value="group"
                />
              </el-select>
            </el-col>
            <el-col :span="10">
              <el-button type="primary" @click="handleSearch">
                <el-icon><Search /></el-icon>
                {{ $t('admin.speakers.search.button') }}
              </el-button>
              <el-button @click="handleReset">
                <el-icon><Refresh /></el-icon>
                {{ $t('common.refresh') }}
              </el-button>
            </el-col>
          </el-row>
        </div>
      </el-card>
    </div>

    <!-- 操作按钮区域 -->
    <div class="action-section">
      <el-card>
        <div class="action-buttons">
          <el-button type="primary" @click="handleAdd">
            <el-icon><Plus /></el-icon>
            {{ $t('admin.speakers.actions.add') }}
          </el-button>
          <el-button type="success" @click="handleRefresh">
            <el-icon><Refresh /></el-icon>
            {{ $t('admin.speakers.actions.refresh') }}
          </el-button>
        </div>
      </el-card>
    </div>

    <!-- 按分组展示 -->
    <div class="table-section">
      <el-card v-loading="loading">
        <div v-if="groupedSpeakers.length === 0" class="empty-text">暂无数据</div>
        <div v-for="group in groupedSpeakers" :key="group.group_name" class="group-section">
          <!-- 分组标题 -->
          <div class="group-header">
            <span class="group-title">{{ group.group_name || '未分组' }}</span>
            <el-tag type="info" size="small">{{ group.speakers.length }} 位演讲者</el-tag>
          </div>
          
          <!-- 演讲者卡片列表 -->
          <div class="speaker-cards">
            <div v-for="speaker in group.speakers" :key="speaker.id" class="speaker-card">
              <div class="speaker-avatar-wrapper">
                <el-avatar :size="50" :src="previewFile(speaker.avatar)" :alt="speaker.name_en">
                  <el-icon size="24"><User /></el-icon>
                </el-avatar>
              </div>
              <div class="speaker-info">
                <h4 class="speaker-name">{{ speaker.name_en }}</h4>
              </div>
              <div class="speaker-actions">
                <el-button type="primary" size="small" @click="handleView(speaker)">查看</el-button>
                <el-button type="warning" size="small" @click="handleEdit(speaker)">编辑</el-button>
                <el-button type="danger" size="small" @click="handleDelete(speaker)">删除</el-button>
              </div>
            </div>
          </div>
        </div>

        <!-- 分页 -->
        <div class="pagination-section">
          <el-pagination v-model:current-page="pagination.page" v-model:page-size="pagination.pageSize"
            :page-sizes="[10, 20, 50, 100]" :total="pagination.total" layout="total, sizes, prev, pager, next, jumper"
            @size-change="handleSizeChange" @current-change="handleCurrentChange">
            <template #total>
              {{ $t('admin.speakers.pagination.total', { total: pagination.total }) }}
            </template>
          </el-pagination>
        </div>
      </el-card>
    </div>

    <!-- 详情弹窗 -->
    <el-dialog v-model="detailVisible" :title="$t('admin.speakers.modal.title')" width="900px"
      :close-on-click-modal="false" class="speaker-detail-dialog">
      <div v-if="currentRecord" class="detail-content">
        <!-- 头部信息区域 -->
        <div class="speaker-header">
          <div class="avatar-section">
            <el-avatar :size="120" :src="previewFile(currentRecord.avatar)" :alt="currentRecord.name_zh"
              class="speaker-avatar">
              <el-icon size="40"><User /></el-icon>
            </el-avatar>
          </div>
          <div class="basic-info">
            <h2 class="speaker-name">{{ currentRecord.name_zh }}</h2>
            <h3 v-if="currentRecord.name_en" class="speaker-name-en">{{ currentRecord.name_en }}</h3>
            <div class="speaker-group" v-if="currentRecord.group_name">
              <el-tag type="primary">{{ currentRecord.group_name }}</el-tag>
            </div>
            <div class="speaker-id">
              <el-tag type="info" size="small">ID: {{ currentRecord.id }}</el-tag>
            </div>
            <div class="created-time">
              <el-icon><Clock /></el-icon>
              <span>{{ $t('admin.speakers.table.createdAt') }}: {{ formatDate(currentRecord.created_at) }}</span>
            </div>
          </div>
        </div>

        <!-- 简介区域 -->
        <div class="bio-section">
          <h3 class="section-title">
            <el-icon><Document /></el-icon>
            {{ $t('admin.speakers.table.bioZh') }}
          </h3>
          <div class="bio-content">
            <p>{{ currentRecord.bio_zh }}</p>
          </div>
        </div>

        <!-- 英文简介区域 -->
        <div v-if="currentRecord.bio_en" class="bio-section">
          <h3 class="section-title">
            <el-icon><Document /></el-icon>
            {{ $t('admin.speakers.table.bioEn') }}
          </h3>
          <div class="bio-content">
            <p>{{ currentRecord.bio_en }}</p>
          </div>
        </div>

        <!-- 视频播放区域 -->
        <div v-if="currentRecord.video_path || currentRecord.video_url" class="video-section">
          <h3 class="section-title">
            <el-icon><VideoPlay /></el-icon>
            {{ $t('admin.speakers.modal.video') }}
          </h3>
          <div class="video-container">
            <video :src="currentRecord.video_url || previewFile(currentRecord.video_path)" controls preload="metadata"
              class="speaker-video">
              {{ $t('admin.speakers.modal.videoNotSupported') }}
            </video>
          </div>
        </div>
      </div>
      <template #footer>
        <div class="dialog-footer">
          <el-button @click="detailVisible = false" size="large">
            {{ $t('admin.speakers.modal.close') }}
          </el-button>
        </div>
      </template>
    </el-dialog>

    <!-- 添加/编辑弹窗 -->
    <el-dialog v-model="formVisible"
      :title="isEdit ? $t('admin.speakers.form.editTitle') : $t('admin.speakers.form.addTitle')" width="600px"
      :close-on-click-modal="false">
      <el-form ref="formRef" :model="form" :rules="formRules" label-width="120px">
        <el-form-item label="分组" prop="group_name">
          <el-select v-model="form.group_name" placeholder="选择或输入分组" filterable allow-create clearable style="width: 100%">
            <el-option
              v-for="group in groupOptions"
              :key="group"
              :label="group"
              :value="group"
            />
          </el-select>
        </el-form-item>
        <el-form-item :label="$t('admin.speakers.form.nameZh')" prop="name_zh">
          <el-input v-model="form.name_zh" :placeholder="$t('admin.speakers.form.nameZhPlaceholder')" />
        </el-form-item>
        <el-form-item :label="$t('admin.speakers.form.nameEn')" prop="name_en">
          <el-input v-model="form.name_en" :placeholder="$t('admin.speakers.form.nameEnPlaceholder')" />
        </el-form-item>
        <el-form-item :label="$t('admin.speakers.form.bioZh')" prop="bio_zh">
          <el-input v-model="form.bio_zh" type="textarea" :rows="4"
            :placeholder="$t('admin.speakers.form.bioZhPlaceholder')" />
        </el-form-item>
        <el-form-item :label="$t('admin.speakers.form.bioEn')" prop="bio_en">
          <el-input v-model="form.bio_en" type="textarea" :rows="4"
            :placeholder="$t('admin.speakers.form.bioEnPlaceholder')" />
        </el-form-item>
        <el-form-item :label="$t('admin.speakers.form.sortOrder')">
          <el-input-number v-model="form.sort_order" :min="1" :max="9999" :step="1" 
            :placeholder="$t('admin.speakers.form.sortOrderPlaceholder')" style="width: 100%;" />
        </el-form-item>
        <el-form-item :label="$t('admin.speakers.form.avatar')">
          <div class="avatar-upload-wrapper" tabindex="0" @paste="handleAvatarPaste">
            <el-upload ref="avatarUploadRef" :auto-upload="false" :show-file-list="false" :on-change="handleAvatarChange"
              :before-upload="beforeAvatarUpload" accept="image/*" drag class="avatar-upload">
              <div class="upload-area">
                <el-icon class="upload-icon"><Upload /></el-icon>
                <div class="upload-text">
                  <p>{{ $t('admin.speakers.form.uploadAvatar') }}</p>
                  <p class="upload-hint">{{ $t('admin.speakers.form.uploadAvatarHint') }}</p>
                  <p class="paste-hint">支持粘贴图片 (Ctrl+V)</p>
                </div>
                <div v-if="form.avatar_temp_path" class="upload-success">
                  <el-icon class="success-icon"><Check /></el-icon>
                  <span>{{ $t('admin.speakers.upload.avatarSuccess') }}</span>
                </div>
              </div>
            </el-upload>
          </div>
        </el-form-item>
        <el-form-item :label="$t('admin.speakers.form.video')">
          <div class="video-upload-section" style="width: 100%;">
            <div class="video-url-input">
              <el-input v-model="form.video_url" :placeholder="$t('admin.speakers.form.videoUrlPlaceholder')" clearable>
                <template #prepend>{{ $t('admin.speakers.form.videoUrl') }}</template>
              </el-input>
            </div>
          </div>
        </el-form-item>
      </el-form>
      <template #footer>
        <el-button @click="formVisible = false">
          {{ $t('common.cancel') }}
        </el-button>
        <el-button type="primary" @click="handleSave" :loading="saving">
          {{ $t('common.save') }}
        </el-button>
      </template>
    </el-dialog>
  </div>
</template>

<script>
import { ref, reactive, computed, onMounted } from 'vue'
import { ElMessage, ElMessageBox } from 'element-plus'
import { Search, Refresh, Plus, Upload, User, Clock, Document, VideoPlay, Check } from '@element-plus/icons-vue'
import { getSpeakers, createSpeaker, updateSpeaker, deleteSpeaker, uploadSpeakerAvatar } from '@/api/speaker'
import { previewFile } from '@/utils/uploadedPreview'
import { useI18n } from 'vue-i18n'

export default {
  name: 'AdminSpeakers',
  components: {
    Search, Refresh, Plus, Upload, User, Clock, Document, VideoPlay, Check
  },
  setup() {
    const { t } = useI18n()

    // 响应式数据
    const loading = ref(false)
    const saving = ref(false)
    const allSpeakers = ref([])
    const detailVisible = ref(false)
    const formVisible = ref(false)
    const isEdit = ref(false)
    const currentRecord = ref(null)
    const formRef = ref(null)

    // 搜索表单
    const searchForm = reactive({
      keyword: '',
      group_name: ''
    })

    // 表单数据
    const form = reactive({
      name_zh: '',
      name_en: '',
      bio_zh: '',
      bio_en: '',
      group_name: '',
      avatar_temp_path: '',
      video_temp_path: '',
      video_url: '',
      sort_order: null
    })

    // 表单验证规则
    const formRules = {
      name_en: [
        { required: true, message: t('admin.speakers.form.nameEnRequired'), trigger: 'blur' },
        { max: 100, message: t('admin.speakers.form.nameEnLength'), trigger: 'blur' }
      ],
      bio_en: [
        { required: true, message: t('admin.speakers.form.bioEnRequired'), trigger: 'blur' },
        { min: 10, message: t('admin.speakers.form.bioEnLength'), trigger: 'blur' }
      ]
    }

    // 分页数据
    const pagination = reactive({
      page: 1,
      pageSize: 100,
      total: 0
    })

    // 计算分组选项
    const groupOptions = computed(() => {
      const groups = new Set()
      allSpeakers.value.forEach(speaker => {
        if (speaker.group_name) {
          groups.add(speaker.group_name)
        }
      })
      return Array.from(groups).sort()
    })

    // 计算分组后的演讲者
    const groupedSpeakers = computed(() => {
      let speakers = [...allSpeakers.value]
      
      // 按分组筛选
      if (searchForm.group_name) {
        speakers = speakers.filter(s => s.group_name === searchForm.group_name)
      }
      
      // 按关键词筛选
      if (searchForm.keyword) {
        const keyword = searchForm.keyword.toLowerCase()
        speakers = speakers.filter(s => 
          (s.name_en && s.name_en.toLowerCase().includes(keyword)) ||
          (s.name_zh && s.name_zh.toLowerCase().includes(keyword)) ||
          (s.bio_en && s.bio_en.toLowerCase().includes(keyword)) ||
          (s.bio_zh && s.bio_zh.toLowerCase().includes(keyword))
        )
      }
      
      // 按分组分组
      const groupMap = {}
      speakers.forEach(speaker => {
        const groupName = speaker.group_name || '未分组'
        if (!groupMap[groupName]) {
          groupMap[groupName] = {
            group_name: groupName,
            speakers: []
          }
        }
        groupMap[groupName].speakers.push(speaker)
      })
      
      // 每个分组内按 sort_order 排序
      Object.values(groupMap).forEach(group => {
        group.speakers.sort((a, b) => {
          const orderA = a.sort_order || 9999
          const orderB = b.sort_order || 9999
          return orderA - orderB
        })
      })
      
      return Object.values(groupMap)
    })

    // 获取数据
    const fetchData = async () => {
      loading.value = true
      try {
        const params = {
          page: 1,
          per_page: 1000,
          keyword: searchForm.keyword || undefined
        }

        const response = await getSpeakers(params)
        if (response.success) {
          allSpeakers.value = response.data
          pagination.total = response.pagination.total
        } else {
          ElMessage.error(response.message || t('common.error'))
        }
      } catch (error) {
        console.error('获取演讲者列表失败:', error)
        ElMessage.error(t('common.error'))
      } finally {
        loading.value = false
      }
    }

    // 搜索
    const handleSearch = () => {
      pagination.page = 1
    }

    // 重置
    const handleReset = () => {
      searchForm.keyword = ''
      searchForm.group_name = ''
      pagination.page = 1
      fetchData()
    }

    // 刷新
    const handleRefresh = () => {
      fetchData()
    }

    // 添加
    const handleAdd = () => {
      isEdit.value = false
      resetForm()
      formVisible.value = true
    }

    // 查看详情
    const handleView = (row) => {
      currentRecord.value = row
      detailVisible.value = true
    }

    // 编辑
    const handleEdit = (row) => {
      isEdit.value = true
      currentRecord.value = row
      Object.assign(form, {
        name_zh: row.name_zh || '',
        name_en: row.name_en || '',
        bio_zh: row.bio_zh || '',
        bio_en: row.bio_en || '',
        group_name: row.group_name || '',
        avatar_temp_path: '',
        video_temp_path: '',
        video_url: row.video_url || '',
        sort_order: row.sort_order
      })
      formVisible.value = true
    }

    // 保存
    const handleSave = async () => {
      if (!formRef.value) return

      try {
        await formRef.value.validate()
        saving.value = true

        const formData = { ...form }
        let response

        if (isEdit.value) {
          response = await updateSpeaker(currentRecord.value.id, formData)
        } else {
          response = await createSpeaker(formData)
        }

        if (response.success) {
          ElMessage.success(t('common.saveSuccess'))
          formVisible.value = false
          fetchData()
        } else {
          ElMessage.error(response.message || t('common.error'))
        }
      } catch (error) {
        console.error('保存失败:', error)
        ElMessage.error(t('common.error'))
      } finally {
        saving.value = false
      }
    }

    // 删除
    const handleDelete = async (row) => {
      try {
        await ElMessageBox.confirm(
          t('common.deleteConfirm'),
          t('common.warning'),
          {
            confirmButtonText: t('common.confirm'),
            cancelButtonText: t('common.cancel'),
            type: 'warning'
          }
        )

        const response = await deleteSpeaker(row.id)
        if (response.success) {
          ElMessage.success(t('common.deleteSuccess'))
          fetchData()
        } else {
          ElMessage.error(response.message || t('common.error'))
        }
      } catch (error) {
        if (error !== 'cancel') {
          console.error('删除失败:', error)
          ElMessage.error(t('common.error'))
        }
      }
    }

    // 头像文件变更处理
    const handleAvatarChange = async (file) => {
      const rawFile = file.raw || file
      if (!beforeAvatarUpload(rawFile)) return

      try {
        const response = await uploadSpeakerAvatar(rawFile)
        if (response.success) {
          form.avatar_temp_path = response.temp_path
          ElMessage.success(t('admin.speakers.upload.avatarSuccess'))
        } else {
          ElMessage.error(response.message || t('admin.speakers.upload.avatarError'))
        }
      } catch (error) {
        console.error('头像上传失败:', error)
        ElMessage.error(t('admin.speakers.upload.avatarError'))
      }
    }

    // 处理粘贴图片
    const handleAvatarPaste = async (event) => {
      event.preventDefault()
      const items = event.clipboardData?.items
      if (!items) return

      for (const item of items) {
        if (item.type.startsWith('image/')) {
          const file = item.getAsFile()
          if (file) {
            if (!beforeAvatarUpload(file)) return
            const pastedFile = {
              name: `pasted-image-${Date.now()}.${file.type.split('/')[1] || 'png'}`,
              raw: file,
              size: file.size,
              type: file.type,
              uid: Date.now()
            }
            await handleAvatarChange(pastedFile)
            ElMessage.success('图片粘贴成功')
            break
          }
        }
      }
    }

    // 头像上传前验证
    const beforeAvatarUpload = (file) => {
      const isImage = file.type.startsWith('image/')
      const isLt2M = file.size / 1024 / 1024 < 2

      if (!isImage) {
        ElMessage.error(t('admin.speakers.upload.avatarTypeError'))
        return false
      }
      if (!isLt2M) {
        ElMessage.error(t('admin.speakers.upload.avatarSizeError'))
        return false
      }
      return true
    }

    // 重置表单
    const resetForm = () => {
      Object.assign(form, {
        name_zh: '',
        name_en: '',
        bio_zh: '',
        bio_en: '',
        group_name: '',
        avatar_temp_path: '',
        video_temp_path: '',
        video_url: '',
        sort_order: null
      })
      if (formRef.value) {
        formRef.value.clearValidate()
      }
    }

    // 分页大小改变
    const handleSizeChange = (size) => {
      pagination.pageSize = size
      pagination.page = 1
    }

    // 当前页改变
    const handleCurrentChange = (page) => {
      pagination.page = page
    }

    // 格式化日期
    const formatDate = (dateString) => {
      if (!dateString) return '-'
      const date = new Date(dateString)
      return date.toLocaleString('zh-CN', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit'
      })
    }

    // 组件挂载时获取数据
    onMounted(() => {
      fetchData()
    })

    return {
      loading,
      saving,
      allSpeakers,
      groupedSpeakers,
      groupOptions,
      detailVisible,
      formVisible,
      isEdit,
      currentRecord,
      form,
      formRef,
      formRules,
      searchForm,
      pagination,
      fetchData,
      handleSearch,
      handleReset,
      handleRefresh,
      handleAdd,
      handleView,
      handleEdit,
      handleSave,
      handleDelete,
      handleAvatarChange,
      handleAvatarPaste,
      beforeAvatarUpload,
      handleSizeChange,
      handleCurrentChange,
      formatDate,
      previewFile
    }
  }
}
</script>

<style scoped>
.admin-speakers {
  padding: 0;
}

.search-section,
.action-section,
.table-section {
  margin-bottom: 20px;
}

.search-form {
  padding: 10px 0;
}

.action-buttons {
  display: flex;
  gap: 10px;
}

/* 分组样式 */
.group-section {
  margin-bottom: 30px;
  padding-bottom: 20px;
  border-bottom: 2px solid #ebeef5;
}

.group-section:last-child {
  border-bottom: none;
  margin-bottom: 0;
}

.group-header {
  display: flex;
  align-items: center;
  gap: 15px;
  margin-bottom: 20px;
  padding: 10px 0;
}

.group-title {
  font-size: 20px;
  font-weight: 600;
  color: #303133;
}

/* 演讲者卡片 */
.speaker-cards {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(180px, 1fr));
  gap: 12px;
}

.speaker-card {
  border: 1px solid #dcdfe6;
  border-radius: 8px;
  padding: 12px;
  background-color: #fff;
  display: flex;
  flex-direction: column;
  align-items: center;
  transition: all 0.3s;
}

.speaker-card:hover {
  box-shadow: 0 2px 12px 0 rgba(0, 0, 0, 0.1);
}

.speaker-avatar-wrapper {
  margin-bottom: 8px;
}

.speaker-info {
  text-align: center;
  margin-bottom: 10px;
  width: 100%;
}

.speaker-name {
  margin: 0;
  font-size: 14px;
  font-weight: 600;
  color: #303133;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.speaker-actions {
  display: flex;
  gap: 4px;
  justify-content: center;

  button {
    width: 33%;
    margin: 0;
  }
}

.empty-text {
  text-align: center;
  color: #909399;
  padding: 40px 0;
  font-size: 14px;
}

.pagination-section {
  margin-top: 20px;
  display: flex;
  justify-content: center;
}

/* 详情弹窗样式 */
.speaker-detail-dialog .el-dialog__body {
  padding: 30px;
}

.detail-content {
  padding: 0;
}

.speaker-header {
  display: flex;
  align-items: flex-start;
  gap: 30px;
  margin-bottom: 30px;
  padding: 25px;
  background: #667eea;
  border-radius: 12px;
  color: white;
}

.avatar-section {
  flex-shrink: 0;
}

.speaker-avatar {
  border: 4px solid rgba(255, 255, 255, 0.3);
}

.basic-info {
  flex: 1;
}

.basic-info .speaker-name {
  color: white;
  font-size: 28px;
}

.basic-info .speaker-name-en {
  color: rgba(255, 255, 255, 0.9);
  font-size: 20px;
  margin: 0 0 15px 0;
}

.speaker-group {
  margin-bottom: 10px;
}

.speaker-id {
  margin-bottom: 15px;
}

.created-time {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  opacity: 0.9;
}

.section-title {
  display: flex;
  align-items: center;
  gap: 10px;
  margin: 0 0 20px 0;
  font-size: 18px;
  font-weight: 600;
  color: #2c3e50;
  padding-bottom: 10px;
  border-bottom: 2px solid #e9ecef;
}

.bio-section {
  margin-bottom: 30px;
}

.bio-content {
  background: #f8f9fa;
  border: 1px solid #e9ecef;
  border-radius: 10px;
  padding: 20px;
}

.bio-content p {
  margin: 0;
  line-height: 1.8;
  color: #495057;
}

.video-section {
  margin-bottom: 20px;
}

.video-container {
  background: #000;
  border-radius: 12px;
  overflow: hidden;
}

.speaker-video {
  width: 100%;
  max-height: 450px;
  display: block;
}

.dialog-footer {
  text-align: center;
  padding-top: 20px;
  border-top: 1px solid #e9ecef;
}

/* 头像上传样式 */
.avatar-upload-wrapper {
  outline: none;
}

.avatar-upload-wrapper:focus .avatar-upload {
  border-color: #409eff;
  box-shadow: 0 0 0 2px rgba(64, 158, 255, 0.2);
}

.avatar-upload .upload-area {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: 100%;
  min-height: 120px;
  transition: all 0.3s ease;
  cursor: pointer;
}

.upload-icon {
  font-size: 32px;
  color: #8c939d;
  margin-bottom: 12px;
}

.upload-text {
  text-align: center;
}

.upload-text p {
  margin: 0;
  color: #606266;
}

.upload-hint {
  font-size: 12px;
  color: #909399;
  margin-top: 4px;
}

.paste-hint {
  font-size: 11px;
  color: #409eff;
  margin-top: 4px;
}

.upload-success {
  display: flex;
  align-items: center;
  margin-top: 12px;
  padding: 8px 16px;
  background-color: #f0f9ff;
  border: 1px solid #b3d8ff;
  border-radius: 4px;
  color: #409eff;
}

.success-icon {
  margin-right: 8px;
  font-size: 16px;
}

/* 响应式设计 */
@media (max-width: 768px) {
  .speaker-cards {
    grid-template-columns: 1fr;
  }
  
  .speaker-header {
    flex-direction: column;
    text-align: center;
  }
  
  .basic-info {
    margin-left: 0;
    margin-top: 20px;
  }
}
</style>
